import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import axios from "axios";
import { toast } from "sonner";
import { 
  Leaf, 
  Users, 
  Clock, 
  Target,
  ArrowRight,
  Loader2,
  X,
  Plus
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewDecision() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stakeholderInput, setStakeholderInput] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    context: "",
    stakeholders: [],
    expected_impact: "",
    timeframe: ""
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addStakeholder = () => {
    if (stakeholderInput.trim() && !formData.stakeholders.includes(stakeholderInput.trim())) {
      setFormData(prev => ({
        ...prev,
        stakeholders: [...prev.stakeholders, stakeholderInput.trim()]
      }));
      setStakeholderInput("");
    }
  };

  const removeStakeholder = (stakeholder) => {
    setFormData(prev => ({
      ...prev,
      stakeholders: prev.stakeholders.filter(s => s !== stakeholder)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.timeframe) {
      toast.error("Veuillez sélectionner un horizon temporel");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/decisions`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Décision analysée avec succès !");
      navigate(`/decision/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'analyse");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 ml-[260px] p-8 bg-background">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              Nouvelle décision
            </h1>
            <p className="text-muted-foreground mt-1">
              Décrivez votre décision pour obtenir une analyse ESG complète
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Basic Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Target className="w-5 h-5 text-primary" />
                    Informations générales
                  </CardTitle>
                  <CardDescription>
                    Décrivez brièvement la décision à analyser
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Titre de la décision *</Label>
                    <Input
                      id="title"
                      placeholder="Ex: Migration vers des énergies renouvelables"
                      value={formData.title}
                      onChange={(e) => handleChange("title", e.target.value)}
                      required
                      data-testid="decision-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description détaillée *</Label>
                    <Textarea
                      id="description"
                      placeholder="Décrivez en détail la nature de cette décision..."
                      value={formData.description}
                      onChange={(e) => handleChange("description", e.target.value)}
                      rows={4}
                      required
                      data-testid="decision-description-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Context */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Leaf className="w-5 h-5 text-chart-1" />
                    Contexte RSE
                  </CardTitle>
                  <CardDescription>
                    Expliquez le contexte organisationnel et environnemental
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="context">Contexte de la décision *</Label>
                    <Textarea
                      id="context"
                      placeholder="Quel est le contexte de cette décision ? Quels facteurs l'ont motivée ?"
                      value={formData.context}
                      onChange={(e) => handleChange("context", e.target.value)}
                      rows={3}
                      required
                      data-testid="decision-context-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expected_impact">Impact attendu *</Label>
                    <Textarea
                      id="expected_impact"
                      placeholder="Quels impacts positifs et négatifs anticipez-vous ?"
                      value={formData.expected_impact}
                      onChange={(e) => handleChange("expected_impact", e.target.value)}
                      rows={3}
                      required
                      data-testid="decision-impact-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Stakeholders & Timeframe */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Users className="w-5 h-5 text-chart-3" />
                    Parties prenantes & Temporalité
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Parties prenantes concernées</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Ex: Employés, Fournisseurs, Clients..."
                        value={stakeholderInput}
                        onChange={(e) => setStakeholderInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addStakeholder())}
                        data-testid="stakeholder-input"
                      />
                      <Button type="button" variant="outline" onClick={addStakeholder}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {formData.stakeholders.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.stakeholders.map((stakeholder) => (
                          <Badge key={stakeholder} variant="secondary" className="px-3 py-1">
                            {stakeholder}
                            <button
                              type="button"
                              onClick={() => removeStakeholder(stakeholder)}
                              className="ml-2 hover:text-destructive"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="timeframe">Horizon temporel *</Label>
                    <Select 
                      value={formData.timeframe} 
                      onValueChange={(value) => handleChange("timeframe", value)}
                    >
                      <SelectTrigger data-testid="timeframe-select">
                        <Clock className="w-4 h-4 mr-2 text-muted-foreground" />
                        <SelectValue placeholder="Sélectionnez l'horizon temporel" />
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={4}>
                        <SelectItem value="court terme" data-testid="timeframe-court">
                          Court terme (moins d'1 an)
                        </SelectItem>
                        <SelectItem value="moyen terme" data-testid="timeframe-moyen">
                          Moyen terme (1 à 3 ans)
                        </SelectItem>
                        <SelectItem value="long terme" data-testid="timeframe-long">
                          Long terme (plus de 3 ans)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      L'horizon temporel aide à qualifier le type de décision (opérationnelle, tactique, stratégique)
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Submit */}
              <div className="flex justify-end gap-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate("/dashboard")}
                  className="rounded-full px-6"
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="rounded-full px-8"
                  disabled={loading}
                  data-testid="submit-decision-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyse en cours...
                    </>
                  ) : (
                    <>
                      Analyser la décision
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
